<template>
  <div class="login-container">
    <!-- 光晕球 -->
    <div class="orb orb-1" />
    <div class="orb orb-2" />
    <div class="orb orb-3" />

    <!-- 漂浮气泡 -->
    <div class="bubble bubble-1" />
    <div class="bubble bubble-2" />
    <div class="bubble bubble-3" />
    <div class="bubble bubble-4" />
    <div class="bubble bubble-5" />
    <div class="bubble bubble-6" />

    <!-- 闪烁星点 -->
    <div class="stars">
      <div v-for="i in 40" :key="i" class="star" :style="getStarStyle(i)" />
    </div>

    <!-- 底部波浪 -->
    <div class="wave-wrap">
      <svg class="wave wave-1" viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path d="M0,60 C240,110 480,10 720,60 C960,110 1200,10 1440,60 L1440,120 L0,120 Z" />
      </svg>
      <svg class="wave wave-2" viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path d="M0,80 C360,20 720,120 1080,40 C1260,0 1380,80 1440,80 L1440,120 L0,120 Z" />
      </svg>
    </div>

    <el-form
      ref="loginForm"
      :model="loginForm"
      :rules="loginRules"
      class="login-form"
      autocomplete="on"
    >
      <div class="login-header">
        <div class="login-logo">
          <i class="el-icon-monitor" />
          <div class="logo-ring" />
        </div>
        <h2 class="login-title">
          <span v-if="!siteLoading">{{ siteName }}</span>
          <span v-else class="site-skeleton" />
        </h2>
        <p class="login-subtitle">欢迎登录，请输入您的账号和密码</p>
      </div>

      <el-form-item prop="username">
        <el-input
          v-model="loginForm.username"
          placeholder="请输入账号"
          prefix-icon="el-icon-user"
          size="large"
          @keyup.enter.native="handleLogin"
        />
      </el-form-item>

      <el-form-item prop="password">
        <el-input
          v-model="loginForm.password"
          :type="passwordVisible ? 'text' : 'password'"
          placeholder="请输入密码"
          prefix-icon="el-icon-lock"
          size="large"
          @keyup.enter.native="handleLogin"
        >
          <i
            slot="suffix"
            :class="passwordVisible ? 'el-icon-view' : 'el-icon-hide'"
            class="password-toggle"
            @click="passwordVisible = !passwordVisible"
          />
        </el-input>
      </el-form-item>

      <el-form-item>
        <el-button
          :loading="loading"
          type="primary"
          class="login-btn"
          @click.native.prevent="handleLogin"
        >
          <span class="btn-text">{{ loading ? '登录中...' : '登 录' }}</span>
          <span class="btn-glow" />
        </el-button>
      </el-form-item>
    </el-form>

    <div class="login-footer">
      <span v-if="!siteLoading">{{ copyright || `Template Admin © ${year}` }}</span>
    </div>
  </div>
</template>

<script>
import { getSiteInfo } from '@/api/system-config'

export default {
  name: 'Login',
  data() {
    return {
      loginForm: { username: '', password: '' },
      loginRules: {
        username: [{ required: true, trigger: 'blur', message: '请输入账号' }],
        password: [{ required: true, trigger: 'blur', message: '请输入密码' }]
      },
      loading: false,
      passwordVisible: false,
      siteLoading: true,
      redirect: undefined
    }
  },
  computed: {
    siteName() {
      return this.$store.getters.siteName
    },
    copyright() {
      return this.$store.getters.copyright
    },
    year() {
      return new Date().getFullYear()
    }
  },
  created() {
    getSiteInfo().then(res => {
      if (res.data) {
        this.$store.dispatch('settings/setSiteInfo', res.data)
      }
    }).catch(() => {}).finally(() => {
      this.siteLoading = false
    })
  },
  watch: {
    $route: {
      handler(route) {
        this.redirect = route.query && route.query.redirect
      },
      immediate: true
    }
  },
  methods: {
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true
          this.$store.dispatch('user/login', this.loginForm)
            .then(() => {
              this.$router.push({ path: this.redirect || '/dashboard' }).catch(() => {})
            })
            .catch(() => {
              this.loading = false
            })
        }
      })
    },
    getStarStyle(i) {
      const seed = i * 137.508
      const x = ((seed * 7) % 100).toFixed(2)
      const y = ((seed * 13) % 100).toFixed(2)
      const size = (((seed * 3) % 2) + 1).toFixed(1)
      const delay = ((seed * 11) % 4).toFixed(2)
      const duration = (((seed * 5) % 3) + 2).toFixed(1)
      return {
        left: `${x}%`,
        top: `${y}%`,
        width: `${size}px`,
        height: `${size}px`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #1a1f3c 0%, #1e3a5f 50%, #0f2d4a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

/* 动态光晕球 */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
  z-index: 0;
}
.orb-1 {
  width: 560px;
  height: 560px;
  background: radial-gradient(circle, rgba(64, 158, 255, 0.22) 0%, transparent 70%);
  top: -180px;
  right: -180px;
  animation: orb-float 8s ease-in-out infinite;
}
.orb-2 {
  width: 450px;
  height: 450px;
  background: radial-gradient(circle, rgba(103, 58, 183, 0.18) 0%, transparent 70%);
  bottom: -120px;
  left: -120px;
  animation: orb-float 10s ease-in-out infinite reverse;
}
.orb-3 {
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(0, 212, 255, 0.12) 0%, transparent 70%);
  top: 45%;
  left: 12%;
  animation: orb-float 12s ease-in-out infinite 2s;
}

@keyframes orb-float {
  0%, 100% { transform: translateY(0) scale(1); }
  50%       { transform: translateY(-30px) scale(1.05); }
}

/* 漂浮气泡 */
.bubble {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  animation: bubble-rise linear infinite;
}
.bubble-1 { width: 60px;  height: 60px;  left: 8%;   bottom: -80px;  animation-duration: 12s; animation-delay: 0s; }
.bubble-2 { width: 30px;  height: 30px;  left: 18%;  bottom: -50px;  animation-duration: 9s;  animation-delay: 1.5s; }
.bubble-3 { width: 80px;  height: 80px;  left: 75%;  bottom: -100px; animation-duration: 14s; animation-delay: 0.8s; }
.bubble-4 { width: 20px;  height: 20px;  left: 85%;  bottom: -40px;  animation-duration: 8s;  animation-delay: 3s; }
.bubble-5 { width: 50px;  height: 50px;  left: 55%;  bottom: -70px;  animation-duration: 11s; animation-delay: 2s; }
.bubble-6 { width: 40px;  height: 40px;  left: 40%;  bottom: -60px;  animation-duration: 10s; animation-delay: 4s; }

@keyframes bubble-rise {
  0%   { transform: translateY(0) translateX(0) scale(1);   opacity: 0; }
  10%  { opacity: 1; }
  50%  { transform: translateY(-50vh) translateX(20px) scale(1.05); }
  90%  { opacity: 0.6; }
  100% { transform: translateY(-105vh) translateX(-10px) scale(0.9); opacity: 0; }
}

/* 闪烁星点 */
.stars {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.star {
  position: absolute;
  border-radius: 50%;
  background: #fff;
  animation: star-twinkle ease-in-out infinite alternate;
}
@keyframes star-twinkle {
  0%   { opacity: 0.1; transform: scale(0.8); }
  100% { opacity: 0.7; transform: scale(1.2); }
}

/* 底部波浪 */
.wave-wrap {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 120px;
  pointer-events: none;
  z-index: 0;
}
.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.wave-1 {
  fill: rgba(64, 158, 255, 0.07);
  animation: wave-move 6s ease-in-out infinite;
}
.wave-2 {
  fill: rgba(54, 207, 201, 0.05);
  animation: wave-move 9s ease-in-out infinite reverse;
}
@keyframes wave-move {
  0%, 100% { transform: translateX(0); }
  50%       { transform: translateX(-30px); }
}

/* 登录卡片 - 白底 */
.login-form {
  width: 420px;
  background: #ffffff;
  border-radius: 20px;
  padding: 44px 40px 36px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.35),
    0 8px 24px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
  animation: card-enter 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;

}

@keyframes card-enter {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 登录头部 */
.login-header {
  text-align: center;
  margin-bottom: 32px;

  .login-logo {
    position: relative;
    width: 68px;
    height: 68px;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1a6fd4, #409eff, #36cfc9);
    border-radius: 50%;
    box-shadow:
      0 6px 20px rgba(64, 158, 255, 0.4),
      0 2px 8px rgba(64, 158, 255, 0.2);
    animation: logo-pulse 3s ease-in-out infinite;

    i {
      font-size: 30px;
      color: #fff;
      position: relative;
      z-index: 1;
    }

    .logo-ring {
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(64, 158, 255, 0.5), rgba(54, 207, 201, 0.2)) border-box;
      -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask-composite: exclude;
      animation: ring-spin 4s linear infinite;
    }
  }

  .login-title {
    font-size: 22px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 8px;
    min-height: 30px;
    letter-spacing: 1px;

    .site-skeleton {
      display: inline-block;
      width: 160px;
      height: 22px;
      border-radius: 4px;
      background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.4s infinite;
      vertical-align: middle;
    }
  }

  .login-subtitle {
    font-size: 13px;
    color: #909399;
    margin: 0;
  }
}

@keyframes logo-pulse {
  0%, 100% { box-shadow: 0 6px 20px rgba(64,158,255,0.4), 0 2px 8px rgba(64,158,255,0.2); }
  50%       { box-shadow: 0 8px 28px rgba(64,158,255,0.6), 0 4px 16px rgba(64,158,255,0.3); }
}

@keyframes ring-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* 表单输入框 */
::v-deep .el-form-item {
  margin-bottom: 20px;

  .el-form-item__error {
    color: #f56c6c;
    padding-top: 3px;
  }
}

::v-deep .el-input {
  .el-input__inner {
    height: 46px;
    line-height: 46px;
    border-radius: 10px;
    background: #f7f8fc;
    border: 1.5px solid #e4e7ed;
    color: #303133;
    font-size: 14px;
    transition: all 0.3s ease;

    &::placeholder { color: #c0c4cc; }

    &:hover {
      border-color: #a0cfff;
      background: #f0f5ff;
    }

    &:focus {
      border-color: #409eff;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.12);
    }
  }

  .el-input__prefix {
    color: #c0c4cc;
    display: flex;
    align-items: center;
  }

  .el-input__suffix {
    display: flex;
    align-items: center;
  }
}

.password-toggle {
  cursor: pointer;
  color: #c0c4cc;
  font-size: 16px;
  transition: color 0.2s;
  &:hover { color: #409eff; }
}

/* 登录按钮 */
.login-btn {
  width: 100%;
  height: 46px;
  border-radius: 10px;
  font-size: 16px;
  letter-spacing: 6px;
  border: none;
  position: relative;
  overflow: hidden;
  background: linear-gradient(90deg, #1a6fd4, #409eff, #36cfc9, #409eff, #1a6fd4);
  background-size: 300% 100%;
  animation: btn-shimmer 4s linear infinite;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  .btn-text {
    position: relative;
    z-index: 1;
  }

  .btn-glow {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(64, 158, 255, 0.45);

    .btn-glow {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: translateY(0);
  }
}

@keyframes btn-shimmer {
  0%   { background-position: 0% 0; }
  100% { background-position: 300% 0; }
}

/* 底部版权 */
.login-footer {
  position: absolute;
  bottom: 24px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  min-height: 18px;
  letter-spacing: 0.5px;
  animation: fade-in 1s ease 0.8s both;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
